/**
 * Bring-in / join screen (task 20.3 / task 18.5).
 *
 * Select a roster character whose currentCampaignId is null and bring it into
 * the campaign (one active character per campaign is enforced server-side).
 */

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { CharacterLedger } from "@rufus/shared-types";
import { useServices } from "../AppContext.js";
import { friendlyErrorMessage } from "../api-client.js";

export function BringInScreen() {
  const { api } = useServices();
  const navigate = useNavigate();
  const { campaignId = "" } = useParams();
  const [roster, setRoster] = useState<readonly CharacterLedger[]>([]);
  const [selected, setSelected] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .listCharacters()
      .then((r) => setRoster(r.filter((c) => c.currentCampaignId == null)))
      .catch((e) => setError(friendlyErrorMessage(e, "Failed to load roster.")));
  }, [api]);

  const bringIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    setError(null);
    setBusy(true);
    try {
      await api.bringIn({
        mode: "select_existing",
        campaignId,
        characterId: selected,
      });
      navigate(`/campaigns/${campaignId}/play`);
    } catch (err) {
      setError(friendlyErrorMessage(err, "Bring-in failed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label="Bring in a character">
      <p>
        <Link to="/campaigns">← Back to campaigns</Link>
      </p>
      <h1>Bring a character into the campaign</h1>
      {error ? <p role="alert">{error}</p> : null}
      <form onSubmit={bringIn}>
        <label htmlFor="character">Available characters</label>
        <select
          id="character"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
        >
          <option value="">Select a character…</option>
          {roster.map((c) => (
            <option key={c.characterId} value={c.characterId}>
              {c.name}
            </option>
          ))}
        </select>
        <button type="submit" disabled={busy || !selected}>
          Bring in
        </button>
      </form>
    </section>
  );
}
